import qs from 'qs';
import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { destroy, getFormValues } from 'redux-form';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { FormUserAcceptInviteData } from 'dashboard/forms/FormUserAcceptInvite/FormUserAcceptInvite';
import SessionClientOnboard from 'dashboard/lib/clients/SessionClientOnboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { User } from 'dashboard/models/user';
import { saveCurrentUser } from 'dashboard/redux/actions/current-user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setOrganization } from 'dashboard/redux/actions/organization';
import { RootState } from 'dashboard/redux/store';
import { expireSession } from 'dashboard/redux/thunk-actions/current-user-session';
import { acceptInvite, loadInvitedUser } from 'dashboard/redux/thunk-actions/user';
import DashboardUserAcceptInviteView from 'dashboard/views/DashboardUserAcceptInviteView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, ApiPayload } from 'lib/interfaces/api';

const DashboardUserAcceptInviteRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activationTokenInvalid, { enable: setActivationTokenInvalid }] = useToggle();

  // We require 2FA during sign-up even for orgs whose 2FA enforcement hasn't
  // taken effect yet.  This reduces the chances of a lockout.
  const mfaRequired = props.organization?.attributes['mfa-required-at'] != null;

  const { user_token: userToken, activation_token: activationToken } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  }) as { user_token: string; activation_token: string };

  useEffect(() => {
    if (
      userToken == null ||
      userToken.length === 0 ||
      activationToken == null ||
      activationToken.length === 0
    ) {
      props.showMessage('Sorry, something went wrong. Please try again.');
      navigate('/dashboard/login', { replace: true });
    }
  }, [userToken, activationToken]);

  const { loading: userLoading } = useApiLoader({
    loader: async () => {
      // Always logout of previous session when creating a new user account.
      await props.expireSession();
      await props.loadInvitedUser(userToken, activationToken);
    },
    deps: [userToken, activationToken],
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: () => {
      navigate('/dashboard/login');
      props.showMessage('Sorry, something went wrong. Please try again.');
    },
  });

  /**
   * Note that each MFA method's logic slightly differs.
   * Email: skip entirely since we trust current user.
   * Phone: additionally requires user to submit phone number. Setup starts after that.
   * TOTP: Setup starts on navigation.
   */

  const { callback: acceptInvite, error: acceptInviteError } = useApiCallback(
    async ({ firstName, lastName, password, passwordConfirmation }: FormUserAcceptInviteData) => {
      await props.acceptInvite({
        firstName,
        lastName,
        password,
        passwordConfirmation,
        userToken,
        activationToken,
      });
      navigate('/dashboard/login');
      props.showMessage('Your account has been activated. Please sign in.', MessageType.Success);
      props.destroyFormState();
    },
    [userToken, activationToken],
  );

  useApiErrorHandler({
    error: acceptInviteError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      if (e instanceof ApiResponseError && (e.status === 400 || e.status === 403)) {
        setActivationTokenInvalid();
      } else {
        props.showMessage('Sorry, something went wrong. Please try again.');
      }
    },
  });

  // There is no setupMfaEmail, because when onboarding we can enable without a
  // code. We implicitly trust the current email address.
  const { callback: enableMfaEmail, error: enableMfaEmailError } = useApiCallback(async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await SessionClientOnboard.postResourceAction<User, ApiPayload<unknown, ApiModel.User>>(
      {
        id: userToken,
        type: ApiModel.User,
      },
      'enable-mfa-email',
      {
        meta: {
          'activation-token': activationToken,
        },
      },
    );
    await acceptInvite(props.acceptInviteFormValues);
  }, [userToken, activationToken, props.acceptInviteFormValues]);

  useApiErrorHandler({
    error: enableMfaEmailError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect activation token':
          props.showMessage(
            'Your activation token is invalid. Please request another invite and try again.',
          );
          break;
        case 'Activation token expired':
          props.showMessage(
            'Your activation token has expired. Please request another invite and try again.',
          );
          break;
        case 'Incorrect confirmation token provided':
          props.showMessage(
            'Your confirmation message is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation token expired':
          props.showMessage(
            'Your confirmation message has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed to enable 2-factor authentication. Please try again.');
      }
    },
  });

  const { callback: setupMfaPhone, error: setupMfaPhoneError } = useApiCallback(
    async (phoneNumber: string) => {
      const { data } = await SessionClientOnboard.postResourceAction<User>(
        {
          id: userToken,
          type: ApiModel.User,
          attributes: {
            'phone-number': phoneNumber,
          },
        },
        'setup-mfa-phone',
        {
          meta: {
            'activation-token': activationToken,
          },
        },
      );
      props.saveCurrentUser(data);
    },
    [userToken],
  );
  const {
    callback: resendMfaPhoneCode,
    error: resendMfaPhoneCodeError,
    loading: resendMfaPhoneCodeLoading,
  } = useApiCallback(async () => {
    await SessionClientOnboard.postResourceAction<User>(
      {
        id: userToken,
        type: ApiModel.User,
      },
      'setup-mfa-phone',
      {
        meta: {
          'activation-token': activationToken,
        },
      },
    );
    props.showMessage("Success. We've sent you another code.", MessageType.Success);
  }, [userToken]);
  useApiErrorHandler({
    error: setupMfaPhoneError || resendMfaPhoneCodeError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      if (e.errors?.[0].title === 'Phone number unsubscribed') {
        return props.showMessage(
          'The provided number is opted out from receiving messages from us. Please try a different number.',
        );
      }
      switch (e.errors?.[0].details) {
        case 'Incorrect activation token':
          props.showMessage(
            'Your activation token is invalid. Please request another invite and try again.',
          );
          break;
        case 'Activation token expired':
          props.showMessage(
            'Your activation token has expired. Please request another invite and try again.',
          );
          break;
        case 'Incorrect confirmation code provided':
          props.showMessage(
            'Your confirmation message is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation code expired':
          props.showMessage(
            'Your confirmation message has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed to enable 2-factor authentication. Please try again.');
      }
    },
  });

  const { callback: enableMfaPhone, error: enableMfaPhoneError } = useApiCallback(
    async (confirmationCode: string) => {
      // Submit MFA
      await SessionClientOnboard.postResourceAction<
        User,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiPayload<unknown, ApiModel.User>,
        { 'confirmation-code': string }
      >(
        {
          id: userToken,
          type: ApiModel.User,
        },
        'enable-mfa-phone',
        {
          meta: {
            'confirmation-code': confirmationCode,
            'activation-token': activationToken,
          },
        },
      );
      // Then, activate user
      await acceptInvite(props.acceptInviteFormValues);
    },
    [userToken, activationToken, props.acceptInviteFormValues],
  );
  useApiErrorHandler({
    error: enableMfaPhoneError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect activation token':
          props.showMessage(
            'Your activation token is invalid. Please request another invite and try again.',
          );
          break;
        case 'Activation token expired':
          props.showMessage(
            'Your activation token has expired. Please request another invite and try again.',
          );
          break;
        case 'Incorrect confirmation code provided':
          props.showMessage(
            'Your confirmation code is invalid. Please request another confirmation and try again.',
          );
          break;
        case 'Confirmation code expired':
          props.showMessage(
            'Your confirmation code has expired. Please request another confirmation and try again.',
          );
          break;
        default:
          props.showMessage('Failed to enable phone 2-factor authentication. Please try again.');
      }
    },
  });

  const { callback: setupMfaTotp, error: setupMfaTotpError } = useApiCallback(async () => {
    // First, start TOTP. We save the updated user because we need the TOTP secret.
    const { data } = await SessionClientOnboard.postResourceAction<User>(
      {
        id: userToken,
        type: ApiModel.User,
      },
      'setup-mfa-totp',
      {
        meta: {
          'activation-token': activationToken,
        },
      },
    );
    props.saveCurrentUser(data);
    // Then, go to TOTP setup page.
    navigate({ pathname: '/dashboard/user/accept-invite/2fa/totp', search: location.search });
  }, []);
  useApiErrorHandler({
    error: setupMfaTotpError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect activation token':
          props.showMessage(
            'Your activation token is invalid. Please request another invite and try again.',
          );
          break;
        case 'Activation token expired':
          props.showMessage(
            'Your activation token has expired. Please request another invite and try again.',
          );
          break;
        case 'User has already enabled TOTP MFA':
          props.showMessage('You have already enabled TOTP 2-factor authentication.');
          break;
        default:
          props.showMessage('Failed to setup TOTP 2-factor authentication. Please try again.');
      }
    },
  });

  const { callback: enableMfaTotp, error: enableMfaTotpError } = useApiCallback(
    async (confirmationCode: string) => {
      await SessionClientOnboard.postResourceAction<
        User,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiPayload<unknown, ApiModel.User>,
        { totp: string }
      >(
        {
          id: userToken,
          type: ApiModel.User,
        },
        'enable-mfa-totp',
        {
          meta: {
            totp: confirmationCode,
            'activation-token': activationToken,
          },
        },
      );
      // Then, activate user
      await acceptInvite(props.acceptInviteFormValues);
    },
    [userToken, activationToken, props.acceptInviteFormValues],
  );
  useApiErrorHandler({
    error: enableMfaTotpError,
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'Incorrect activation token':
          props.showMessage(
            'Your activation token is invalid. Please request another invite and try again.',
          );
          break;
        case 'Activation token expired':
          props.showMessage(
            'Your activation token has expired. Please request another invite and try again.',
          );
          break;
        case 'Incorrect TOTP code provided':
          props.showMessage('Your provided code is invalid. Please try again.');
          break;
        default:
          props.showMessage('Failed to enable TOTP 2-factor authentication. Please try again.');
      }
    },
  });

  if (userLoading) {
    return <LoadingIndicator fullscreen={true} />;
  }

  return (
    <DashboardUserAcceptInviteView
      activationTokenInvalid={activationTokenInvalid}
      currentUser={props.currentUser}
      hasUserDetails={props.acceptInviteFormValues != null}
      mfaRequired={mfaRequired}
      onAcceptInvite={acceptInvite}
      onEnableMfaEmail={enableMfaEmail}
      onEnableMfaPhone={enableMfaPhone}
      onEnableMfaTotp={enableMfaTotp}
      onResendMfaPhoneCode={resendMfaPhoneCode}
      onSetupMfaPhone={setupMfaPhone}
      onSetupMfaTotp={setupMfaTotp}
      resendMfaPhoneCodeLoading={resendMfaPhoneCodeLoading}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: state.currentUser.currentUser,
  organization: state.organization.organization,
  acceptInviteFormValues: getFormValues('userAcceptInvite')(state) as FormUserAcceptInviteData,
});

const mapDispatchToProps = {
  showMessage,
  loadInvitedUser,
  acceptInvite,
  saveCurrentUser,
  setOrganization,
  expireSession,
  destroyFormState: () => destroy('userAcceptInvite'),
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserAcceptInviteRoute);
